// thien-viet-admin/src/features/backup/index.jsx
import { DeleteOutlined, DownloadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Modal, Space, Table, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import SH_Toolbar from '../../components/SH_Toolbar';
import backupApi from '../../api/backup';

function formatBytes(bytes) {
	if (!bytes && bytes !== 0) return '';
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
	if (bytes === 0) return '0 B';
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	const val = bytes / Math.pow(1024, i);
	return `${val.toFixed(i === 0 ? 0 : 2)} ${sizes[i]}`;
}

function saveBlobAsFile(blob, filename) {
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

const TV_backup = () => {
	const [loading, setLoading] = useState(false);
	const [backups, setBackups] = useState([]);
	const [search, setSearch] = useState('');

	const fetchBackups = async () => {
		try {
			setLoading(true);
			const res = await backupApi.list();
			setBackups(res.data?.backups || res.data?.data?.backups || res.data?.data || []);
		} catch (e) {
			message.error(e?.response?.data?.message || e.message || 'Không tải được danh sách backup');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchBackups();
	}, []);

	const filtered = useMemo(() => {
		const s = (search || '').trim().toLowerCase();
		if (!s) return backups;
		return backups.filter((x) => (x.filename || '').toLowerCase().includes(s));
	}, [backups, search]);

	const handleCreateBackup = async () => {
		try {
			setLoading(true);
			await backupApi.create();
			message.success('Tạo backup thành công');
			await fetchBackups();
		} catch (e) {
			message.error(e?.response?.data?.message || e.message || 'Tạo backup thất bại');
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async (filename) => {
		try {
			setLoading(true);
			const res = await backupApi.download(filename);
			const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/gzip' });
			saveBlobAsFile(blob, filename);
			message.success('Đã tải file backup');
		} catch (e) {
			message.error(e?.response?.data?.message || e.message || 'Tải backup thất bại');
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadLatest = async () => {
		try {
			setLoading(true);
			const res = await backupApi.downloadLatest();

			const filename = `latest_backup_${dayjs().format('YYYY-MM-DD_HH-mm')}.archive.gz`;

			const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/gzip' });
			saveBlobAsFile(blob, filename);
			message.success('Đã tải backup mới nhất');
		} catch (e) {
			message.error(e?.response?.data?.message || e.message || 'Tải backup mới nhất thất bại');
		} finally {
			setLoading(false);
		}
	};

	const confirmDelete = (record) => {
		Modal.confirm({
			title: 'Xác nhận xoá backup',
			content: `Bạn có chắc chắn muốn xoá file "${record.filename}" không?`,
			okText: 'Xoá',
			okButtonProps: { danger: true },
			cancelText: 'Huỷ',
			onOk: async () => {
				try {
					setLoading(true);
					await backupApi.delete(record.filename);
					message.success('Đã xoá backup');
					await fetchBackups();
				} catch (e) {
					message.error(e?.response?.data?.message || e.message || 'Xoá backup thất bại');
				} finally {
					setLoading(false);
				}
			},
		});
	};

	const columns = [
		{
			title: 'Tên file',
			dataIndex: 'filename',
			key: 'filename',
			render: (t) => <span style={{ fontFamily: 'monospace' }}>{t}</span>,
		},
		{
			title: 'Kích thước',
			dataIndex: 'sizeBytes',
			key: 'sizeBytes',
			width: 140,
			render: (v) => <span>{formatBytes(v)}</span>,
			sorter: (a, b) => (a.sizeBytes || 0) - (b.sizeBytes || 0),
			sortDirections: ['descend', 'ascend'],
		},
		{
			title: 'Cập nhật lúc',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 200,
			render: (v) => <span>{v ? dayjs(v).format('HH:mm A DD/MM/YYYY') : ''}</span>,
			sorter: (a, b) => dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
			defaultSortOrder: 'descend',
			sortDirections: ['descend', 'ascend'],
		},
		{
			title: 'Thao tác',
			key: 'action',
			width: 260,
			render: (_, record) => (
				<Space>
					<Button icon={<DownloadOutlined />} onClick={() => handleDownload(record.filename)}>
						Tải
					</Button>
					<Button danger type="dashed" icon={<DeleteOutlined />} onClick={() => confirmDelete(record)}>
						Xoá
					</Button>
				</Space>
			),
		},
	];

	return (
		<>
			<Title level={2}>Backup DB</Title>

			<SH_Toolbar
				left={() => (
					<Space>
						<Button icon={<PlusOutlined />} type="primary" onClick={handleCreateBackup} loading={loading}>
							Tạo backup ngay
						</Button>
						<Button icon={<DownloadOutlined />} onClick={handleDownloadLatest} loading={loading}>
							Tải latest
						</Button>
						<Button icon={<ReloadOutlined />} onClick={fetchBackups} loading={loading}>
							Refresh
						</Button>
					</Space>
				)}
				right={() => (
					<Flex>
						<Input.Search
							allowClear
							style={{ width: 260 }}
							placeholder="Tìm theo tên file backup"
							onChange={(e) => setSearch(e.target.value)}
						/>
					</Flex>
				)}
			/>

			<Table
				className="mt-2"
				loading={loading}
				columns={columns}
				dataSource={filtered}
				rowKey={(r) => r.filename}
				pagination={{ pageSize: 10 }}
				scroll={{ x: 1300 }}
			/>
		</>
	);
};

export default TV_backup;
